import { AppreciationPrice } from "./ReaderAppreciation";
import { displayLabel } from "../../utils/displayLabel";
import TbrButton from "../ui/TbrButton";
// components/public/Bookstore.js
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

// CHANGE THIS to your public/master books API
// It should accept `?host=<subdomain host>` and return { books: [...] }
const BOOKS_API =
  "https://9y92mrhq2b.execute-api.us-west-2.amazonaws.com/default/authorshopbooks";

function BookCover({ src, alt }) {
  const [failed, setFailed] = useState(false);
  return failed ? <span className="p-4 font-serif text-center text-bookish-purple">Cover unavailable</span> : <img src={src} alt={alt} className="max-h-full max-w-full object-contain p-2" loading="lazy" onError={() => setFailed(true)} />;
}

export default function Bookstore({ books: booksProp, groupCatalogue = false }) {
  const [books, setBooks] = useState(Array.isArray(booksProp) ? booksProp : []);
  const [loading, setLoading] = useState(!Array.isArray(booksProp));
  const [error, setError] = useState("");

  const host = useMemo(
    () => (typeof window !== "undefined" ? window.location.host : ""),
    []
  );

  useEffect(() => {
    if (Array.isArray(booksProp)) return; // props override; skip fetch

    const controller = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(
          `${BOOKS_API}?host=${encodeURIComponent(host)}`,
          { signal: controller.signal }
        );
        if (!res.ok) throw new Error(`Books API ${res.status}`);

        // Expecting { books: [...] } already filtered for this author/host
        const data = await res.json();
        const list = Array.isArray(data?.books) ? data.books : [];

        // Optional: filter only public books if API doesn't do it already
        const publicOnly = list.filter((b) => b?.public !== false);

        setBooks(publicOnly);
      } catch (e) {
        console.error("Failed to load public books:", e);
        setError("Failed to load books.");
        setBooks([]);
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [booksProp, host]);

  const visibleBooks = Array.isArray(booksProp) ? booksProp : books;

  const slugify = (title = "") =>
    String(title)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const toProductUrl = (book) => {
    // Prefer explicit productUrl coming from your master data
    if (book?.productUrl) return book.productUrl;

    // Fallback: build a local URL under this subdomain
    const slug = slugify(book?.title || "book");
    return `https://${host}/shop/${slug}`;
  };

  // If the product URL is on the same host as the current page,
  // return a relative path (e.g., "/shop/slug") so React Router
  // renders it inside the layout without a full reload.
  const toInternalPath = (absUrl) => {
    try {
      const u = new URL(absUrl);
      if (u.host === host) {
        return `${u.pathname}${u.search}${u.hash || ""}`;
      }
    } catch {
      // if it's already a relative path, just return it
      if (typeof absUrl === "string" && absUrl.startsWith("/")) return absUrl;
    }
    return null;
  };

  const S3_REGION = "us-west-2";

  const objectUrl = (bucket, key) =>
    `https://${bucket}.s3.${S3_REGION}.amazonaws.com/${encodeURI(key)}`;

  const getNumericPrice = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
  };

  const sortFormatsByPrice = (formats = []) =>
    [...formats].sort((a, b) => {
      const priceDiff = getNumericPrice(a?.price) - getNumericPrice(b?.price);
      if (priceDiff !== 0) return priceDiff;
      return String(a?.type || "").localeCompare(String(b?.type || ""));
    });

  const getLowestFormat = (book) => sortFormatsByPrice(book?.formats || [])[0] || null;

  // --------- ONLY CHANGE: make cover picking more robust (and fix objUrl->objectUrl) ---------
  const pickCoverUrl = (book) => {
    if (!book) return "";

    const lowestFormat = getLowestFormat(book);

    if (typeof lowestFormat?.coverImageUrl === "string" && lowestFormat.coverImageUrl.trim()) {
      return lowestFormat.coverImageUrl.trim();
    }

    if (typeof book.coverImageUrl === "string" && book.coverImageUrl.trim()) {
      return book.coverImageUrl.trim();
    }
    if (typeof book.imageUrl === "string" && book.imageUrl.trim()) {
      return book.imageUrl.trim();
    }

    if (Array.isArray(book.formats)) {
      const fmtWithUrl = sortFormatsByPrice(book.formats).find(
        (f) => typeof f?.coverImageUrl === "string" && f.coverImageUrl.trim()
      );
      if (fmtWithUrl?.coverImageUrl) return fmtWithUrl.coverImageUrl.trim();
    }

    if (Array.isArray(book.coverImages) && book.coverImages.length) {
      const last = book.coverImages[book.coverImages.length - 1];
      if (typeof last === "string" && last.startsWith("http")) return last;
      if (typeof last?.url === "string" && last.url) return last.url;
      if (last?.masterBucket && last?.masterKey) return objectUrl(last.masterBucket, last.masterKey);
      if (last?.authorBucket && last?.authorKey) return objectUrl(last.authorBucket, last.authorKey);
    }

    if (Array.isArray(book.formats)) {
      for (const f of sortFormatsByPrice(book.formats)) {
        const imgs = Array.isArray(f?.coverImages) && f.coverImages.length
          ? f.coverImages
          : Array.isArray(f?.images) && f.images.length
          ? f.images
          : null;

        if (imgs) {
          const last = imgs[imgs.length - 1];
          if (typeof last === "string" && last.startsWith("http")) return last;
          if (typeof last?.url === "string" && last.url) return last.url;
          if (last?.masterBucket && last?.masterKey) return objectUrl(last.masterBucket, last.masterKey);
          if (last?.authorBucket && last?.authorKey) return objectUrl(last.authorBucket, last.authorKey);
        }

        const img0 = f?.coverImages?.[0] || f?.images?.[0];
        if (typeof img0 === "string" && img0.startsWith("http")) return img0;
        if (img0?.url && typeof img0.url === "string") return img0.url;
        if (img0?.masterBucket && img0?.masterKey) return objectUrl(img0.masterBucket, img0.masterKey);
        if (img0?.authorBucket && img0?.authorKey) return objectUrl(img0.authorBucket, img0.authorKey);
      }
    }

    return "";
  };
  // -------------------------------------------------------------------------------------------

  // ------- Group by series (A→Z), sort within by numeric bookNumber -------
  const { seriesGroups, standalones } = useMemo(() => {
    const bySeries = new Map();
    const singles = [];

    for (const b of visibleBooks) {
      const raw = (b?.series || "").trim();
      if (!raw) {
        singles.push(b);
        continue;
      }
      const key = `${groupCatalogue ? b.subdomain || '' : ''}::${raw.toLowerCase()}`;
      if (!bySeries.has(key)) bySeries.set(key, { key, name: raw, authorName: groupCatalogue ? b.authorName : '', items: [] });
      bySeries.get(key).items.push(b);
    }

    // sort each group's items by bookNumber numeric; fallback by title
    for (const g of bySeries.values()) {
      g.items.sort((a, b) => {
        const an = parseFloat(a?.bookNumber);
        const bn = parseFloat(b?.bookNumber);
        const aValid = !Number.isNaN(an);
        const bValid = !Number.isNaN(bn);
        if (aValid && bValid) return an - bn;
        if (aValid) return -1;
        if (bValid) return 1;
        return String(a?.title || "").localeCompare(String(b?.title || ""), undefined, { sensitivity: "base" });
      });
      g.total = g.items.length;
    }

    // sort groups A→Z by series name (natural)
    const groups = Array.from(bySeries.values()).sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
    );

    return { seriesGroups: groups, standalones: singles };
  }, [visibleBooks, groupCatalogue]);

  // Reusable card for a single book
  const BookCard = ({ book, seriesTotal }) => {
    const cover = pickCoverUrl(book);
    const productUrl = toProductUrl(book);
    const internalPath = toInternalPath(productUrl);
    const sortedFormats = sortFormatsByPrice(book?.formats || []);
    const previewFormats = sortedFormats.slice(0, 2);

    const BookLink = ({ className, children }) =>
      internalPath ? (
        <Link to={internalPath} className={className}>
          {children}
        </Link>
      ) : (
        <a href={productUrl} className={className}>
          {children}
        </a>
      );

    return (
      <article className="border border-bookish-purple/20 bg-bookish-white rounded-lg shadow-sm flex flex-col overflow-hidden">
        {/* Cover clickable */}
        {cover ? (
          <BookLink className="block">
            <div className="w-full h-48 sm:h-64 flex items-center justify-center bg-white">
              <BookCover key={cover} src={cover} alt={displayLabel(book?.title || "Book cover")} />
            </div>
          </BookLink>
        ) : (
          <BookLink className="block">
            <div className="w-full h-64 flex items-center justify-center bg-bookish-cream text-bookish-purple/50 border-b border-bookish-purple/10">
              No Cover Image
            </div>
          </BookLink>
        )}

        <div className="p-4 flex flex-col gap-2 flex-1">
          {/* Title clickable */}
          <h3 className="text-xl font-serif font-bold text-bookish-purple">
            <BookLink className="hover:underline hover:text-bookish-purple">
              {displayLabel(book?.title || "Untitled Book")}
            </BookLink>
          </h3>

          {groupCatalogue && book.authorName && <p className="text-sm text-bookish-purple/80">by {book.authorName}</p>}

          {/* Series line: "Series Name: 1 of N" */}
          {book?.series ? (
            <p className="text-sm text-bookish-purple/80">
              {displayLabel(book.series)}{Number(book.bookNumber) > 0 ? (groupCatalogue ? ": Book " + book.bookNumber : ": " + book.bookNumber + " of " + (seriesTotal ?? "?")) : ""}
            </p>
          ) : null}

          {book?.tagline ? (
            <p className="text-sm italic text-bookish-purpleAccent">{book.tagline}</p>
          ) : null}

          {/* Formats & Prices */}
          {previewFormats.length > 0 ? (
          <div className="mt-1 text-sm text-bookish-purple space-y-1">
            {previewFormats.map((f, i) => (
              <div key={`${f?.type || "format"}-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">{f?.type || "Format"}</span>
                  <span>
                    <AppreciationPrice price={f?.price} format={f?.type} host={book?.subdomain ? book.subdomain.replace(/^https?:\/\//, '').replace(/\/.*$/, '') : book?.productUrl ? new URL(book.productUrl, window.location.origin).hostname : window.location.hostname} />                  </span>
                </div>

                {f?.shipping?.freeShipping && (
                  <p className="text-xs font-semibold text-bookish-purpleAccent">
                    Free Shipping
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : null}  
          <div className="mt-auto pt-2 flex items-center gap-2">
            <TbrButton book={book} productUrl={productUrl} coverImageUrl={cover} compact />
            <BookLink className="flex-1 text-center bg-bookish-purple text-white px-4 py-2 rounded-md hover:bg-bookish-purpleAccent transition">
              View Book
            </BookLink>
            {/*<BookLink className="flex-1 text-center border border-bookish-purple text-bookish-purple px-4 py-2 rounded-md hover:bg-bookish-purple/5 transition">
              Buy Now
            </BookLink>*/}
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="min-h-[calc(100vh-0px)] bg-bookish-cream text-bookish-royal px-6 py-12">
      <div className="max-w-6xl mx-auto">
        {/* <header className="mb-8 text-center">
          <h1 className="text-3xl md:text-5xl font-fancy text-bookish-royal">Bookstore</h1>
          <p className="mt-2 text-bookish-purpleAccent">
            Explore available books, editions, and formats.
          </p>
        </header> */}

        {loading && <div className="text-center text-bookish-purpleAccent">Loading…</div>}
        {error && <div className="text-center text-red-600">{error}</div>}

        {!loading && !error && (
          <>
            {visibleBooks.length === 0 ? (
              <div className="text-center text-bookish-purpleAccent">No books are available yet.</div>
            ) : (
              <div className="space-y-10">
                {/* Series groups */}
                {seriesGroups.map((g) => (
                  <section key={g.key}>
                    <h2 className="text-2xl font-serif font-bold text-bookish-purple mb-4">
                      {displayLabel(g.name)}{g.authorName && <span className="block text-base font-normal mt-1">by {g.authorName}</span>} <span className="text-base font-normal text-bookish-purple/70">( {g.total} )</span>
                    </h2>
                    <div className="bg-gradient-to-b from-bookish-white/60 to-bookish-white/30 rounded-2xl px-6 py-6 shadow-sm ring-1 ring-bookish-purple/10 border border-bookish-purple/10 mx-auto max-w-6xl">

                    <div className="grid gap-3 sm:gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 items-stretch">
                      {g.items.map((b, i) => (
                        <BookCard key={`${g.name}-${i}-${b.title}`} book={b} seriesTotal={g.total} />
                      ))}
                    </div>
                    </div>
                  </section>
                ))}

                {/* Standalones */}
                {standalones.length > 0 && (
                  <section>
                    <h2 className="text-2xl font-serif font-bold text-bookish-purple mb-4">Standalones</h2>
                    <div className="bg-gradient-to-b from-bookish-white/60 to-bookish-white/30 rounded-2xl px-6 py-6 shadow-sm ring-1 ring-bookish-purple/10 border border-bookish-purple/10 mx-auto max-w-6xl">

                    <div className="grid gap-3 sm:gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 items-stretch">
                      {standalones.map((b, i) => (
                        <BookCard key={`standalone-${i}-${b.title}`} book={b} seriesTotal={null} />
                      ))}
                    </div>
                    </div>
                  </section>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
